import React, { useState, useRef, useEffect } from 'react';
import { useModalLayer } from '../../lib/modalStack';
import { Type } from 'lucide-react';
import CaseFormatSelector, { CASE_FORMATS, type CaseFormat } from './CaseFormatSelector';

interface ChangeCaseDropdownProps {
  /** Called with the selected case format. The parent applies the transformation. */
  onSelectFormat: (format: CaseFormat) => void;
  /** Optional: show only when text is selected */
  disabled?: boolean;
}

export default function ChangeCaseDropdown({ onSelectFormat, disabled }: ChangeCaseDropdownProps) {
  const [open, setOpen] = useState(false);
  // SYS-11: layer assigned by the modal stack while open (the z scale
  // has no generated named utilities in Tailwind v4 — overlays register
  // with the stack instead; see index.css).
  const layerZ = useModalLayer(open);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); if (!disabled) setOpen(prev => !prev); }}
        className={`p-1.5 rounded ${disabled ? 'text-slate-300 cursor-not-allowed' : 'hover:bg-slate-200 text-slate-600 cursor-pointer'}`}
        title="Change Case"
      >
        <Type className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1" style={{ zIndex: layerZ }}>
          <CaseFormatSelector
            selected="original"
            onChange={(fmt) => {
              onSelectFormat(fmt);
              setOpen(false);
            }}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Applies a case transformation to the selected text in a contentEditable element.
 * Returns true if any text was transformed.
 */
export function applyCaseToSelection(format: CaseFormat): boolean {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || !sel.rangeCount) return false;

  const range = sel.getRangeAt(0);
  const selectedText = range.toString();
  if (!selectedText) return false;

  // Apply case formatting
  let transformed: string;
  switch (format) {
    case 'upper': transformed = selectedText.toUpperCase(); break;
    case 'lower': transformed = selectedText.toLowerCase(); break;
    case 'sentence':
      transformed = selectedText.charAt(0).toUpperCase() + selectedText.slice(1).toLowerCase();
      break;
    case 'title':
    case 'capitalize':
      transformed = selectedText.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
      break;
    case 'toggle':
      transformed = selectedText.replace(/./g, (c) => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase());
      break;
    default:
      return false;
  }

  // Replace the selected text with the transformed version
  range.deleteContents();
  range.insertNode(document.createTextNode(transformed));
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
}
